/**
 * ComplianceAlerts — Alertes conformité RH.
 * Fin CDD · Période essai · Titre séjour · Visite médicale · Entretien pro · Ancienneté.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldAlert, AlertTriangle, Info, Bell, RefreshCw, Filter, X,
  Clock, FileClock, IdCard, Stethoscope, MessageSquare, MessageSquareWarning,
  Award, ChevronRight, BellOff, Loader2, Mail, Send, Settings2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ICONS = {
  cdd_end_soon: FileClock,
  probation_end_soon: Clock,
  residence_permit_expiring: IdCard,
  medical_visit_due: Stethoscope,
  interview_pro_due: MessageSquare,
  interview_pro_overdue: MessageSquareWarning,
  anniversary_milestone: Award,
};

const SEV_CLS = {
  critical: {
    bg: "bg-red-50 border-red-200",
    text: "text-red-700",
    badge: "bg-red-100 text-red-700 border-red-200",
    icon: "text-red-600",
  },
  warning: {
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-700",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    icon: "text-amber-600",
  },
  info: {
    bg: "bg-blue-50 border-blue-200",
    text: "text-blue-700",
    badge: "bg-blue-100 text-blue-700 border-blue-200",
    icon: "text-blue-600",
  },
};

const SEV_LABEL = { critical: "Critique", warning: "À surveiller", info: "À anticiper" };

const TYPE_OPTIONS = [
  { value: "", label: "Tous les types" },
  { value: "cdd_end_soon", label: "Fin de CDD" },
  { value: "probation_end_soon", label: "Période essai" },
  { value: "residence_permit_expiring", label: "Titre séjour" },
  { value: "medical_visit_due", label: "Visite médicale" },
  { value: "interview_pro_due", label: "Entretien pro" },
  { value: "interview_pro_overdue", label: "Entretien pro en retard" },
  { value: "anniversary_milestone", label: "Ancienneté" },
];

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "—";

function DismissDialog({ open, onOpenChange, alert, onDone }) {
  const [reason, setReason] = useState("");
  const [snooze, setSnooze] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (permanent) => {
    setBusy(true);
    try {
      await api.post("/hr/compliance/alerts/dismiss", {
        employee_id: alert.employee_id,
        alert_type: alert.type,
        ref_key: alert.ref_key,
        reason: reason || null,
        snooze_until: permanent ? null : (snooze || null),
      });
      toast.success(permanent ? "Alerte ignorée" : "Alerte reportée");
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!alert) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ignorer / reporter cette alerte</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="p-3 bg-slate-50 rounded border">
            <p className="font-medium">{alert.type_label}</p>
            <p className="text-xs text-slate-500 mt-0.5">{alert.employee_name}</p>
            <p className="text-xs text-slate-600 mt-1">{alert.detail}</p>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Motif (optionnel)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Décision reportée, remplacement…" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Reporter jusqu'au</Label>
            <Input type="date" value={snooze} onChange={(e) => setSnooze(e.target.value)}
              data-testid="snooze-date" />
            <p className="text-[11px] text-slate-500 mt-1">
              Laisser vide pour ignorer définitivement.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          {snooze ? (
            <Button onClick={() => submit(false)} disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="snooze-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <BellOff className="w-4 h-4 mr-2" />}
              Reporter au {fmtDate(snooze)}
            </Button>
          ) : (
            <Button onClick={() => submit(true)} disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="dismiss-permanent-btn">
              {busy ? "..." : "Ignorer définitivement"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState("");
  const [type, setType] = useState("");
  const [dismissAlert, setDismissAlert] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, cRes] = await Promise.all([
        api.get("/hr/compliance/alerts", { params: { severity: severity || undefined,
                                                        type: type || undefined,
                                                        horizon_days: 180 } }),
        api.get("/hr/compliance/alerts/counts"),
      ]);
      setAlerts(aRes.data.alerts || []);
      setCounts(cRes.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [severity, type]);

  const bySeverity = useMemo(() => {
    const g = { critical: [], warning: [], info: [] };
    alerts.forEach(a => { (g[a.severity] || []).push(a); });
    return g;
  }, [alerts]);

  return (
    <div className="space-y-6" data-testid="compliance-alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <ShieldAlert className="w-6 h-6 text-slate-700" />
            Alertes de conformité RH
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fin CDD · Période essai · Titre de séjour · Visite médicale ·
            Entretien pro · Ancienneté — pour éviter tout retard réglementaire
          </p>
        </div>
        <div className="flex gap-2">
          <WeeklyDigestButton />
          <Button variant="outline" onClick={load}
            data-testid="refresh-alerts">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualiser
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiCard label="Critique" value={counts?.counts?.critical || 0}
          icon={AlertTriangle} sev="critical" />
        <KpiCard label="À surveiller" value={counts?.counts?.warning || 0}
          icon={Bell} sev="warning" />
        <KpiCard label="À anticiper" value={counts?.counts?.info || 0}
          icon={Info} sev="info" />
        <KpiCard label="Total actives" value={counts?.counts?.total || 0}
          icon={ShieldAlert} sev="info" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
              <Filter className="w-3.5 h-3.5" /> Filtres
            </div>
            <Select value={severity || "__all__"}
              onValueChange={(v) => setSeverity(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="filter-severity">
                <SelectValue placeholder="Sévérité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes sévérités</SelectItem>
                <SelectItem value="critical">Critique</SelectItem>
                <SelectItem value="warning">À surveiller</SelectItem>
                <SelectItem value="info">À anticiper</SelectItem>
              </SelectContent>
            </Select>
            <Select value={type || "__all__"}
              onValueChange={(v) => setType(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map(o =>
                  <SelectItem key={o.value || "__all__"} value={o.value || "__all__"}>
                    {o.label}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {(severity || type) && (
              <Button variant="ghost" size="sm"
                onClick={() => { setSeverity(""); setType(""); }}>
                <X className="w-3.5 h-3.5 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alerts list — groupées par sévérité */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />)}
        </div>
      ) : alerts.length === 0 ? (
        <Alert className="bg-emerald-50 border-emerald-200">
          <ShieldAlert className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-sm text-emerald-800">
            Aucune alerte active — tu es à jour sur toutes les échéances RH.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {["critical", "warning", "info"].map(sev => {
            const items = bySeverity[sev] || [];
            if (items.length === 0) return null;
            const cls = SEV_CLS[sev];
            return (
              <Card key={sev}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <span className={cls.icon}>
                      {sev === "critical" ? <AlertTriangle className="w-4 h-4" />
                        : sev === "warning" ? <Bell className="w-4 h-4" />
                          : <Info className="w-4 h-4" />}
                    </span>
                    {SEV_LABEL[sev]}
                    <Badge variant="outline" className={cls.badge}>{items.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {items.map((a, i) => (
                      <AlertRow key={a.id} alert={a} idx={i}
                        onView={() => navigate(`/app/hr/employees/${a.employee_id}`)}
                        onDismiss={() => setDismissAlert(a)} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DismissDialog open={!!dismissAlert} onOpenChange={(v) => !v && setDismissAlert(null)}
        alert={dismissAlert} onDone={load} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widget "Rappel automatique hebdomadaire" — config destinataire + envoi manuel
// ---------------------------------------------------------------------------
function WeeklyDigestButton() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [runs, setRuns] = useState([]);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/hr/compliance/weekly-digest/settings"),
        api.get("/hr/compliance/weekly-digest/runs"),
      ]);
      setSettings(s.data);
      setRecipient(s.data.recipient_email || "");
      setCcInput((s.data.cc_emails || []).join(", "));
      setEnabled(s.data.enabled !== false);
      setRuns(r.data.runs || []);
      setLastRun((r.data.runs || [])[0] || null);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      const cc = ccInput.split(",").map(s => s.trim()).filter(Boolean);
      await api.put("/hr/compliance/weekly-digest/settings", {
        recipient_email: recipient || null,
        cc_emails: cc,
        enabled,
      });
      toast.success("Paramètres du digest enregistrés");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!recipient) {
      toast.error("Renseigne d'abord un email destinataire");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/hr/compliance/weekly-digest/send-now");
      if (data.sent) {
        toast.success(`Digest envoyé à ${data.to} (${data.critical_count} alerte(s) critique(s))`);
      } else if (data.skipped) {
        toast.info(`Digest ignoré : ${data.reason}`);
      } else {
        toast.error(data.error || "Envoi échoué");
      }
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
        data-testid="weekly-digest-open">
        <Mail className="w-4 h-4 mr-1.5" />
        Rappel hebdo
        {lastRun?.ok && (
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-700">
            <CheckCircle2 className="w-3 h-3" /> {lastRun.iso_week}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Rappel hebdomadaire par email
            </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
              Chaque <strong>lundi matin</strong>, Averdy Agency envoie automatiquement un email récapitulant
              toutes les alertes RH critiques ouvertes (fin CDD, période essai, visite médicale,
              titre séjour, entretien pro). Renseigne le destinataire pour ne rien laisser dormir.
            </div>

            <div>
              <Label className="text-xs uppercase text-slate-500">Destinataire (email)</Label>
              <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                placeholder="rh@monentreprise.fr" type="email"
                data-testid="weekly-digest-recipient" />
              {settings && !settings.configured && recipient && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Valeur reprise depuis les rappels acomptes — enregistre pour valider.
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase text-slate-500">Copie (CC, virgules)</Label>
              <Input value={ccInput} onChange={(e) => setCcInput(e.target.value)}
                placeholder="direction@monentreprise.fr, cfo@..."
                data-testid="weekly-digest-cc" />
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="digest-enabled" checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                data-testid="weekly-digest-enabled" />
              <Label htmlFor="digest-enabled" className="text-xs">
                Activer le rappel automatique du lundi
              </Label>
            </div>

            {runs.length > 0 && (
              <div className="border rounded-md">
                <div className="px-3 py-2 bg-slate-50 border-b text-[11px] uppercase text-slate-500 tracking-wide">
                  Derniers envois
                </div>
                <div className="max-h-40 overflow-auto divide-y">
                  {runs.slice(0, 5).map((r) => (
                    <div key={r.id} className="px-3 py-2 flex items-center justify-between text-xs">
                      <div>
                        <div className="font-medium text-slate-800">{r.iso_week}</div>
                        <div className="text-slate-500">
                          {new Date(r.sent_at).toLocaleString("fr-FR")} · {r.to}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          {r.critical_count} crit.
                        </Badge>
                        {r.ok ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Envoyé
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                            Échec
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Fermer</Button>
            <Button variant="outline" onClick={sendNow} disabled={sending || !recipient}
              data-testid="weekly-digest-send-now">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                       : <Send className="w-4 h-4 mr-2" />}
              Envoyer maintenant
            </Button>
            <Button onClick={save} disabled={busy}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="weekly-digest-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KpiCard({ label, value, icon: Icon, sev }) {
  const cls = SEV_CLS[sev] || SEV_CLS.info;
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-2 border",
          cls.bg, cls.icon)}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </CardContent>
    </Card>
  );
}

function AlertRow({ alert, idx, onView, onDismiss }) {
  const cls = SEV_CLS[alert.severity];
  const Icon = ICONS[alert.type] || Bell;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(idx, 10) * 0.02, duration: 0.2 }}
      className="p-3 hover:bg-slate-50 flex items-center gap-3"
      data-testid={`alert-row-${alert.type}-${idx}`}
    >
      <div className={cn("w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
        cls.bg, cls.icon)}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900">{alert.employee_name}</span>
          <Badge variant="outline" className={cn("text-[10px]", cls.badge)}>
            {alert.type_label}
          </Badge>
          <span className={cn("text-[11px] font-medium", cls.text)}>
            · {alert.days_label}
          </span>
          {alert.target_date && (
            <span className="text-[11px] text-slate-500">
              · {fmtDate(alert.target_date)}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-600 mt-0.5 truncate">{alert.detail}</p>
        {alert.employee_job && (
          <p className="text-[10px] text-slate-400 mt-0.5">{alert.employee_job}</p>
        )}
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <Button size="sm" variant="ghost" onClick={onView}
          data-testid={`view-alert-${idx}`}>
          <ChevronRight className="w-3.5 h-3.5 mr-1" /> Voir
        </Button>
        <Button size="sm" variant="ghost"
          onClick={onDismiss}
          className="text-slate-500 hover:text-slate-900"
          data-testid={`dismiss-alert-${idx}`}>
          <BellOff className="w-3.5 h-3.5 mr-1" /> Ignorer
        </Button>
      </div>
    </motion.div>
  );
}
